import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { TableOrderProvider } from './lib/table-context';
import { KitchenPauseProvider } from './lib/kitchen-pause-context';
import { LanguageProvider } from './lib/i18n';
import { KitchenPausedBanner } from './components/restaurant/KitchenPausedBanner';
import { getSubdomainSlug } from './lib/subdomain';
import { ScrollToTop } from './components/ui/ScrollToTop';
// Customer Pages (Eager - Critical Path)
import { HomePage } from './pages/HomePage';
import { RestaurantListingPage } from './pages/RestaurantListingPage';
import { RestaurantPage } from './pages/RestaurantPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
// Customer Pages (Lazy)
const MenuPage = lazy(() => import('./pages/MenuPage').then(m => ({
  default: m.MenuPage
})));
const CartPage = lazy(() => import('./pages/CartPage').then(m => ({
  default: m.CartPage
})));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then(m => ({
  default: m.CheckoutPage
})));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage').then(m => ({
  default: m.OrderConfirmationPage
})));
const TrackingPage = lazy(() => import('./pages/TrackingPage').then(m => ({
  default: m.TrackingPage
})));
const TablePage = lazy(() => import('./pages/TablePage').then(m => ({
  default: m.TablePage
})));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({
  default: m.OnboardingPage
})));
const TermsRestaurantsPage = lazy(() => import('./pages/TermsRestaurantsPage').then(m => ({
  default: m.TermsRestaurantsPage
})));
const NatuCafePage = lazy(() => import('./pages/NatuCafePage').then(m => ({
  default: m.NatuCafePage
})));
// Admin Pages (Lazy)
const LoginPage = lazy(() => import('./pages/admin/LoginPage').then(m => ({
  default: m.LoginPage
})));
const NoizaMetricsPage = lazy(() => import('./pages/admin/NoizaMetricsPage').then(m => ({
  default: m.NoizaMetricsPage
})));
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage').then(m => ({
  default: m.DashboardPage
})));
const KDSPage = lazy(() => import('./pages/admin/KDSPage').then(m => ({
  default: m.KDSPage
})));
const InventoryPage = lazy(() => import('./pages/admin/InventoryPage').then(m => ({
  default: m.InventoryPage
})));
const MetricsPage = lazy(() => import('./pages/admin/MetricsPage').then(m => ({
  default: m.MetricsPage
})));
const MenuManagementPage = lazy(() => import('./pages/admin/MenuManagementPage').then(m => ({
  default: m.MenuManagementPage
})));
const ImportPage = lazy(() => import('./pages/admin/ImportPage').then(m => ({
  default: m.ImportPage
})));
const StationSelectPage = lazy(() => import('./pages/admin/StationSelectPage').then(m => ({
  default: m.StationSelectPage
})));
const CajaOverviewPage = lazy(() => import('./pages/admin/CajaOverviewPage').then(m => ({
  default: m.CajaOverviewPage
})));
const CancelledOrdersPage = lazy(() => import('./pages/admin/CancelledOrdersPage').then(m => ({
  default: m.CancelledOrdersPage
})));
const TableManagementPage = lazy(() => import('./pages/admin/TableManagementPage').then(m => ({
  default: m.TableManagementPage
})));
const TableQRPage = lazy(() => import('./pages/admin/TableQRPage').then(m => ({
  default: m.TableQRPage
})));
const LoyaltyDashboardPage = lazy(() => import('./pages/admin/LoyaltyDashboardPage').then(m => ({
  default: m.LoyaltyDashboardPage
})));
const LoyaltyRedemptionPage = lazy(() => import('./pages/admin/LoyaltyRedemptionPage').then(m => ({
  default: m.LoyaltyRedemptionPage
})));
const CreateRestaurantPage = lazy(() => import('./pages/admin/CreateRestaurantPage').then(m => ({
  default: m.CreateRestaurantPage
})));
const DriversListPage = lazy(() => import('./pages/admin/DriversListPage').then(m => ({
  default: m.DriversListPage
})));
const CreateDriverPage = lazy(() => import('./pages/admin/CreateDriverPage').then(m => ({
  default: m.CreateDriverPage
})));
const VehiclesListPage = lazy(() => import('./pages/admin/VehiclesListPage').then(m => ({
  default: m.VehiclesListPage
})));
const CreateVehiclePage = lazy(() => import('./pages/admin/CreateVehiclePage').then(m => ({
  default: m.CreateVehiclePage
})));
const AssignmentsPage = lazy(() => import('./pages/admin/AssignmentsPage').then(m => ({
  default: m.AssignmentsPage
})));
const UsersAdminPage = lazy(() => import('./pages/admin/UsersAdminPage').then(m => ({
  default: m.UsersAdminPage
})));
const EnrollmentPage = lazy(() => import('./pages/admin/EnrollmentPage').then(m => ({
  default: m.EnrollmentPage
})));
const EnrollmentRestaurantPage = lazy(() => import('./pages/admin/EnrollmentRestaurantPage').then(m => ({
  default: m.EnrollmentRestaurantPage
})));
const EnrollmentMenuUpdatePage = lazy(() => import('./pages/admin/EnrollmentMenuUpdatePage').then(m => ({
  default: m.EnrollmentMenuUpdatePage
})));
const EnrollmentPlatformAdminPage = lazy(() => import('./pages/admin/EnrollmentPlatformAdminPage').then(m => ({
  default: m.EnrollmentPlatformAdminPage
})));
const EnrollmentMenuImagesPage = lazy(() => import('./pages/admin/EnrollmentMenuImagesPage').then(m => ({
  default: m.EnrollmentMenuImagesPage
})));
const ReceiptGeneratorPage = lazy(() => import('./pages/admin/ReceiptGeneratorPage').then(m => ({
  default: m.ReceiptGeneratorPage
})));
const GlobalAdminLoginPage = lazy(() => import('./pages/admin/GlobalAdminLoginPage').then(m => ({
  default: m.GlobalAdminLoginPage
})));
import { RequireAdminAuth } from './components/admin/RequireAdminAuth';
import { RequirePlatformAdmin } from './components/admin/RequirePlatformAdmin';
// Driver App (Lazy)
const DriverLoginPage = lazy(() => import('./pages/driver/DriverLoginPage').then(m => ({
  default: m.DriverLoginPage
})));
const DriverAppPage = lazy(() => import('./pages/driver/DriverAppPage').then(m => ({
  default: m.DriverAppPage
})));
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
// ─── MAINTENANCE GATE ───
// Slugs under maintenance — only accessible with ?dev=noiza2026
const MAINTENANCE_SLUGS = ['noiza.restaurant'];
function MaintenancePage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const handleUnlock = () => {
    if (code === 'noiza2026') {
      sessionStorage.setItem('noiza_dev_access', 'true');
      window.location.reload();
    } else {
      setError(true);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-0">
      <div className="max-w-md w-full text-center" data-id="element-1">
        <div className="w-16 h-16 mx-auto mb-6 bg-[#141414] rounded-2xl flex items-center justify-center border border-white/10" data-id="element-2">
          <span className="text-2xl" data-id="element-3">🔧</span>
        </div>
        <h1 className="text-2xl font-black text-white mb-2" data-id="element-4">
          En Mantenimiento
        </h1>
        <p className="text-white/40 text-sm mb-8" data-id="element-5">
          Este restaurante está temporalmente en modo de pruebas. Volveremos
          pronto.
        </p>
        <div className="space-y-3" data-id="element-6">
          <input type="password" placeholder="Código de acceso" value={code} onChange={e => {
          setCode(e.target.value);
          setError(false);
        }} onKeyDown={e => e.key === 'Enter' && handleUnlock()} className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent text-center" data-id="element-7" />
          {error && <p className="text-[#CE1126] text-xs" data-id="element-8">Código incorrecto</p>}
          <button onClick={handleUnlock} className="w-full py-3 rounded-xl bg-[#006847] text-white font-bold text-sm hover:bg-[#005a3d] transition-colors" data-id="element-9">
            Acceder
          </button>
        </div>
      </div>
    </div>;
}
/** Routes Capricho de Canela to PublicMenuPage (premium theme), others to RestaurantPage */
function SmartRestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Maintenance gate: check URL param or session
  if (slug && MAINTENANCE_SLUGS.includes(slug)) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasDevParam = urlParams.get('dev') === 'noiza2026';
    const hasSessionAccess = sessionStorage.getItem('noiza_dev_access') === 'true';
    if (hasDevParam && !hasSessionAccess) {
      sessionStorage.setItem('noiza_dev_access', 'true');
    }
    if (!hasDevParam && !hasSessionAccess) {
      return <MaintenancePage data-id="element-10" />;
    }
  }
  if (slug === 'capricho-de-canela') {
    return <PublicMenuPage data-id="element-11" />;
  }
  return <RestaurantPage data-id="element-12" />;
}
/** Redirects /admin/:slug → /admin/:slug/operacion (station select) */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/operacion`} replace data-id="element-13" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/operacion" replace data-id="element-14" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-15">{children}</TenantProvider>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <SubdomainTenantProvider slug={subdomainSlug} data-id="element-16">
        <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-18">
              <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-19"></div>
            </div>} data-id="element-17">
          <Routes data-id="element-20">
            {/* Customer Routes */}
            <Route path="/" element={<SmartRestaurantPage data-id="element-22" />} data-id="element-21" />
            <Route path="/menu" element={<MenuPage data-id="element-24" />} data-id="element-23" />
            <Route path="/cart" element={<CartPage data-id="element-26" />} data-id="element-25" />
            <Route path="/checkout" element={<CheckoutPage data-id="element-28" />} data-id="element-27" />
            <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-30" />} data-id="element-29" />
            <Route path="/tracking/:orderId" element={<TrackingPage data-id="element-32" />} data-id="element-31" />
            <Route path="/t/:tableId" element={<TablePage data-id="element-34" />} data-id="element-33" />

            {/* Admin Routes */}
            <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-36" />} data-id="element-35" />
            <Route path="/admin/login" element={<LoginPage data-id="element-38" />} data-id="element-37" />
            <Route path="/admin/operacion" element={<RequireAdminAuth data-id="element-40">
                  <StationSelectPage data-id="element-41" />
                </RequireAdminAuth>} data-id="element-39" />
            <Route path="/admin/dashboard" element={<RequireAdminAuth data-id="element-43">
                  <DashboardPage data-id="element-44" />
                </RequireAdminAuth>} data-id="element-42" />
            <Route path="/admin/kds" element={<RequireAdminAuth data-id="element-46">
                  <KDSPage data-id="element-47" />
                </RequireAdminAuth>} data-id="element-45" />
            <Route path="/admin/caja" element={<RequireAdminAuth data-id="element-49">
                  <CajaOverviewPage data-id="element-50" />
                </RequireAdminAuth>} data-id="element-48" />
            <Route path="/admin/cancelled" element={<RequireAdminAuth data-id="element-52">
                  <CancelledOrdersPage data-id="element-53" />
                </RequireAdminAuth>} data-id="element-51" />
            <Route path="/admin/inventory" element={<RequireAdminAuth data-id="element-55">
                  <InventoryPage data-id="element-56" />
                </RequireAdminAuth>} data-id="element-54" />
            <Route path="/admin/metrics" element={<RequireAdminAuth data-id="element-58">
                  <MetricsPage data-id="element-59" />
                </RequireAdminAuth>} data-id="element-57" />
            <Route path="/admin/menu" element={<RequireAdminAuth data-id="element-61">
                  <MenuManagementPage data-id="element-62" />
                </RequireAdminAuth>} data-id="element-60" />
            <Route path="/admin/import" element={<RequireAdminAuth data-id="element-64">
                  <ImportPage data-id="element-65" />
                </RequireAdminAuth>} data-id="element-63" />
            <Route path="/admin/tables" element={<RequireAdminAuth data-id="element-67">
                  <TableManagementPage data-id="element-68" />
                </RequireAdminAuth>} data-id="element-66" />
            <Route path="/admin/qr" element={<RequireAdminAuth data-id="element-70">
                  <TableQRPage data-id="element-71" />
                </RequireAdminAuth>} data-id="element-69" />
            <Route path="/admin/loyalty" element={<RequireAdminAuth data-id="element-73">
                  <LoyaltyDashboardPage data-id="element-74" />
                </RequireAdminAuth>} data-id="element-72" />
            <Route path="/admin/loyalty/redemption" element={<RequireAdminAuth data-id="element-76">
                  <LoyaltyRedemptionPage data-id="element-77" />
                </RequireAdminAuth>} data-id="element-75" />

            {/* Platform Admin Routes (Subdomain) */}
            <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-79">
                  <ReceiptGeneratorPage data-id="element-80" />
                </RequirePlatformAdmin>} data-id="element-78" />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace data-id="element-82" />} data-id="element-81" />
          </Routes>
        </Suspense>
      </SubdomainTenantProvider>;
  }
  // ─── MARKETPLACE MODE ───
  // When accessed via noizacr.com
  return <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-84">
          <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-85"></div>
        </div>} data-id="element-83">
      <Routes data-id="element-86">
        {/* Marketplace Routes */}
        <Route path="/" element={<HomePage data-id="element-88" />} data-id="element-87" />
        <Route path="/restaurants" element={<RestaurantListingPage data-id="element-90" />} data-id="element-89" />
        <Route path="/terminos-restaurantes" element={<TermsRestaurantsPage data-id="element-92" />} data-id="element-91" />
        <Route path="/natucafe" element={<NatuCafePage data-id="element-94" />} data-id="element-93" />
        <Route path="/onboarding" element={<OnboardingPage data-id="element-96" />} data-id="element-95" />

        {/* Restaurant Specific Routes */}
        <Route path="/r/:slug" element={<SmartRestaurantPage data-id="element-98" />} data-id="element-97" />
        <Route path="/r/:slug/menu" element={<MenuPage data-id="element-100" />} data-id="element-99" />
        <Route path="/r/:slug/cart" element={<CartPage data-id="element-102" />} data-id="element-101" />
        <Route path="/r/:slug/checkout" element={<CheckoutPage data-id="element-104" />} data-id="element-103" />
        <Route path="/r/:slug/order-confirmation" element={<OrderConfirmationPage data-id="element-106" />} data-id="element-105" />
        <Route path="/r/:slug/tracking/:orderId" element={<TrackingPage data-id="element-108" />} data-id="element-107" />
        <Route path="/r/:slug/t/:tableId" element={<TablePage data-id="element-110" />} data-id="element-109" />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<GlobalAdminLoginPage data-id="element-112" />} data-id="element-111" />
        <Route path="/admin/metrics" element={<RequirePlatformAdmin data-id="element-114">
              <NoizaMetricsPage data-id="element-115" />
            </RequirePlatformAdmin>} data-id="element-113" />
        <Route path="/admin/restaurants/new" element={<RequirePlatformAdmin data-id="element-117">
              <CreateRestaurantPage data-id="element-118" />
            </RequirePlatformAdmin>} data-id="element-116" />
        <Route path="/admin/drivers" element={<RequirePlatformAdmin data-id="element-120">
              <DriversListPage data-id="element-121" />
            </RequirePlatformAdmin>} data-id="element-119" />
        <Route path="/admin/drivers/new" element={<RequirePlatformAdmin data-id="element-123">
              <CreateDriverPage data-id="element-124" />
            </RequirePlatformAdmin>} data-id="element-122" />
        <Route path="/admin/vehicles" element={<RequirePlatformAdmin data-id="element-126">
              <VehiclesListPage data-id="element-127" />
            </RequirePlatformAdmin>} data-id="element-125" />
        <Route path="/admin/vehicles/new" element={<RequirePlatformAdmin data-id="element-129">
              <CreateVehiclePage data-id="element-130" />
            </RequirePlatformAdmin>} data-id="element-128" />
        <Route path="/admin/assignments" element={<RequirePlatformAdmin data-id="element-132">
              <AssignmentsPage data-id="element-133" />
            </RequirePlatformAdmin>} data-id="element-131" />
        <Route path="/admin/users" element={<RequirePlatformAdmin data-id="element-135">
              <UsersAdminPage data-id="element-136" />
            </RequirePlatformAdmin>} data-id="element-134" />
        <Route path="/admin/enrollment" element={<RequirePlatformAdmin data-id="element-138">
              <EnrollmentPage data-id="element-139" />
            </RequirePlatformAdmin>} data-id="element-137" />
        <Route path="/admin/enrollment/restaurant" element={<RequirePlatformAdmin data-id="element-141">
              <EnrollmentRestaurantPage data-id="element-142" />
            </RequirePlatformAdmin>} data-id="element-140" />
        <Route path="/admin/enrollment/menu-update" element={<RequirePlatformAdmin data-id="element-144">
              <EnrollmentMenuUpdatePage data-id="element-145" />
            </RequirePlatformAdmin>} data-id="element-143" />
        <Route path="/admin/enrollment/platform-admin" element={<RequirePlatformAdmin data-id="element-147">
              <EnrollmentPlatformAdminPage data-id="element-148" />
            </RequirePlatformAdmin>} data-id="element-146" />
        <Route path="/admin/enrollment/menu-images" element={<RequirePlatformAdmin data-id="element-150">
              <EnrollmentMenuImagesPage data-id="element-151" />
            </RequirePlatformAdmin>} data-id="element-149" />
        <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-153">
              <ReceiptGeneratorPage data-id="element-154" />
            </RequirePlatformAdmin>} data-id="element-152" />

        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-156" />} data-id="element-155" />
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-158" />} data-id="element-157" />
        <Route path="/admin/:slug/operacion" element={<RequireAdminAuth data-id="element-160">
              <StationSelectPage data-id="element-161" />
            </RequireAdminAuth>} data-id="element-159" />
        <Route path="/admin/:slug/dashboard" element={<RequireAdminAuth data-id="element-163">
              <DashboardPage data-id="element-164" />
            </RequireAdminAuth>} data-id="element-162" />
        <Route path="/admin/:slug/kds" element={<RequireAdminAuth data-id="element-166">
              <KDSPage data-id="element-167" />
            </RequireAdminAuth>} data-id="element-165" />
        <Route path="/admin/:slug/caja" element={<RequireAdminAuth data-id="element-169">
              <CajaOverviewPage data-id="element-170" />
            </RequireAdminAuth>} data-id="element-168" />
        <Route path="/admin/:slug/cancelled" element={<RequireAdminAuth data-id="element-172">
              <CancelledOrdersPage data-id="element-173" />
            </RequireAdminAuth>} data-id="element-171" />
        <Route path="/admin/:slug/inventory" element={<RequireAdminAuth data-id="element-175">
              <InventoryPage data-id="element-176" />
            </RequireAdminAuth>} data-id="element-174" />
        <Route path="/admin/:slug/metrics" element={<RequireAdminAuth data-id="element-178">
              <MetricsPage data-id="element-179" />
            </RequireAdminAuth>} data-id="element-177" />
        <Route path="/admin/:slug/menu" element={<RequireAdminAuth data-id="element-181">
              <MenuManagementPage data-id="element-182" />
            </RequireAdminAuth>} data-id="element-180" />
        <Route path="/admin/:slug/import" element={<RequireAdminAuth data-id="element-184">
              <ImportPage data-id="element-185" />
            </RequireAdminAuth>} data-id="element-183" />
        <Route path="/admin/:slug/tables" element={<RequireAdminAuth data-id="element-187">
              <TableManagementPage data-id="element-188" />
            </RequireAdminAuth>} data-id="element-186" />
        <Route path="/admin/:slug/qr" element={<RequireAdminAuth data-id="element-190">
              <TableQRPage data-id="element-191" />
            </RequireAdminAuth>} data-id="element-189" />
        <Route path="/admin/:slug/loyalty" element={<RequireAdminAuth data-id="element-193">
              <LoyaltyDashboardPage data-id="element-194" />
            </RequireAdminAuth>} data-id="element-192" />
        <Route path="/admin/:slug/loyalty/redemption" element={<RequireAdminAuth data-id="element-196">
              <LoyaltyRedemptionPage data-id="element-197" />
            </RequireAdminAuth>} data-id="element-195" />

        {/* Driver App Routes */}
        <Route path="/driver/login" element={<DriverLoginPage data-id="element-199" />} data-id="element-198" />
        <Route path="/driver/app" element={<DriverAppPage data-id="element-201" />} data-id="element-200" />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace data-id="element-203" />} data-id="element-202" />
      </Routes>
    </Suspense>;
}
export function App() {
  return <LanguageProvider data-id="element-204">
      <CartProvider data-id="element-205">
        <TableOrderProvider data-id="element-206">
          <KitchenPauseProvider data-id="element-207">
            <BrowserRouter data-id="element-208">
              <ScrollToTop data-id="element-209" />
              <KitchenPausedBanner data-id="element-210" />
              <div className="min-h-screen w-full flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-211">
                <AppRoutes data-id="element-212" />
              </div>
            </BrowserRouter>
          </KitchenPauseProvider>
        </TableOrderProvider>
      </CartProvider>
    </LanguageProvider>;
}